import React, { useState, useMemo } from 'react';
import Card from './Card.js';
import CardDetail from './CardDetail.js';
import { getUILoc } from '../utils/cardUtils.js';
import audioManager from '../utils/AudioManager.js';

const CardLibraryScreen = ({ cardDefs, onBack }) => {
  const [sortBy, setSortBy] = useState('implementation');
  const [filterType, setFilterType] = useState('all');
  const [filterScale, setFilterScale] = useState('all');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showTokens, setShowTokens] = useState(false);

  const filteredAndSortedCards = useMemo(() => {
    let cards = Object.values(cardDefs);

    // トークン表示フィルター
    if (!showTokens) {
      cards = cards.filter(card => !card.is_token);
    }

    // 有効フラグフィルター
    cards = cards.filter(card => card.is_enabled !== false);

    // フィルタリング
    if (filterType !== 'all') {
      const typeNum = parseInt(filterType);
      cards = cards.filter(card => card.card_type === typeNum);
    }

    if (filterScale !== 'all') {
      cards = cards.filter(card => {
        const scale = card.required_scale;
        switch (filterScale) {
          case '0-2':
            return scale >= 0 && scale <= 2;
          case '3-15':
            return scale >= 3 && scale <= 15;
          case '16+':
            return scale >= 16;
          default:
            return true;
        }
      });
    }

    // ソート
    cards.sort((a, b) => {
      if (sortBy === 'requiredScale') {
        return a.required_scale - b.required_scale;
      }
      return (a.definitionOrder || 0) - (b.definitionOrder || 0);
    });

    return cards;
  }, [cardDefs, sortBy, filterType, filterScale, showTokens]);

  const enabledCardCount = useMemo(() => {
    return Object.values(cardDefs).filter(card => card.is_enabled !== false).length;
  }, [cardDefs]);

  const handleBack = () => {
    audioManager.playSE('cancel');
    onBack();
  };

  const handleCardClick = (card) => {
    audioManager.playSE('select');
    setSelectedCard(card);
  };

  const handleSortChange = (e) => {
    audioManager.playSE('select');
    setSortBy(e.target.value);
  };

  const handleTypeFilterChange = (e) => {
    audioManager.playSE('select');
    setFilterType(e.target.value);
  };

  const handleScaleFilterChange = (e) => {
    audioManager.playSE('select');
    setFilterScale(e.target.value);
  };

  const handleToggleTokens = () => {
    audioManager.playSE('select');
    setShowTokens(prev => !prev);
  };

  return (
    <div className="card-library-screen">
      <div className="screen-header">
        <div className="header-left">
          <h1>{getUILoc('SCREENS.ARCHIVE')}</h1>
        </div>
        <button className="back-button" onClick={handleBack}>
          ← {getUILoc('BUTTONS.BACK')}
        </button>
      </div>

      <div className="library-controls">
        <div className="control-group">
          <label>{getUILoc('LABELS.SORT_BY')}:</label>
          <select value={sortBy} onChange={handleSortChange}>
            <option value="implementation">{getUILoc('LABELS.SORT_IMPLEMENTATION')}</option>
            <option value="requiredScale">{getUILoc('LABELS.SORT_SCALE')}</option>
          </select>
        </div>

        <div className="control-group">
          <label>{getUILoc('LABELS.CARD_TYPE')}:</label>
          <select value={filterType} onChange={handleTypeFilterChange}>
            <option value="all">{getUILoc('LABELS.FILTER_ALL_TYPE')}</option>
            <option value="1">{getUILoc('CARD_TYPES.1')}</option>
            <option value="2">{getUILoc('CARD_TYPES.2')}</option>
            <option value="3">{getUILoc('CARD_TYPES.3')}</option>
          </select>
        </div>

        <div className="control-group">
          <label>{getUILoc('LABELS.REQUIRED_SCALE')}:</label>
          <select value={filterScale} onChange={handleScaleFilterChange}>
            <option value="all">{getUILoc('LABELS.FILTER_ALL_SCALE')}</option>
            <option value="0-2">0-2</option>
            <option value="3-15">3-15</option>
            <option value="16+">16+</option>
          </select>
        </div>
        
        <div className="library-stats">
          {getUILoc('LABELS.DISPLAYING')}: {filteredAndSortedCards.length} / {enabledCardCount}
        </div>

        <div className="control-group">
          <button
            className={`token-toggle-button ${showTokens ? 'active' : ''}`}
            onClick={handleToggleTokens}
          >
            {getUILoc('LABELS.SHOW_TOKENS') || 'トークン'}：{showTokens ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      <div className="card-grid">
        {filteredAndSortedCards.map((card) => (
          <div key={card.id} className="card-library-wrapper">
            <Card 
              card={card}
              mode="library"
              onClick={() => handleCardClick(card)}
            />
          </div>
        ))}
      </div>

      {selectedCard && (
        <CardDetail
          card={selectedCard}
          cardDefs={Object.values(cardDefs)}
          onClose={() => {
            audioManager.playSE('cancel');
            setSelectedCard(null);
          }}
        />
      )}
    </div>
  );
};

export default CardLibraryScreen;
